import type { QueryClient } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { normalizeAnalyticsPageIds } from 'app/modules/analytics/utils/normalizeAnalyticsPageIds';
import store from 'store';
import { PageThunk } from 'modules/page/pageActions';
import type { Dispatch } from 'types/Store';

interface StoredSyncRun {
  syncRunSince: string | null;
  syncRunUntil: string | null;
  syncRunPageIdsNormalized: readonly number[];
}

const ANALYTICS_QUERY_NAMESPACE = 'analytics';
const SYNC_REFRESHED_SECTIONS = new Set<string>([
  'overview',
  'performance',
  'content',
  'audience',
]);

/**
 * Matches any analytics data query (overview/performance/content/audience) for
 * the given period whose page ids are all within the synced set, regardless of
 * the trailing key segments (`'time_series'`, `compareSince`, `compareUntil`,
 * content `extra`). Refreshing every section — not just performance/audience —
 * means the tab the user is currently on updates in place after a sync, and
 * comparison-enabled queries match too (fixed compare-less keys never would).
 */
function matchesSyncedAnalyticsQuery(
  queryKeyValue: unknown,
  syncedPageIds: ReadonlySet<number>,
  since: string,
  until: string,
): boolean {
  if (!Array.isArray(queryKeyValue)) {
    return false;
  }
  const [namespace, section, inner] = queryKeyValue as unknown[];
  if (namespace !== ANALYTICS_QUERY_NAMESPACE) {
    return false;
  }
  if (typeof section !== 'string' || !SYNC_REFRESHED_SECTIONS.has(section)) {
    return false;
  }
  if (!Array.isArray(inner)) {
    return false;
  }
  const [pageIds, keySince, keyUntil] = inner as unknown[];
  if (keySince !== since || keyUntil !== until) {
    return false;
  }
  if (!Array.isArray(pageIds) || pageIds.length === 0) {
    return false;
  }
  return pageIds.every((id) => typeof id === 'number' && syncedPageIds.has(id));
}

export function invalidateAnalyticsSyncRunQueries(
  queryClient: QueryClient,
  run: StoredSyncRun,
): void {
  const since = run.syncRunSince;
  const until = run.syncRunUntil;
  const pageIdsNorm = run.syncRunPageIdsNormalized;
  if (
    since == null ||
    until == null ||
    pageIdsNorm == null ||
    pageIdsNorm.length === 0
  ) {
    return;
  }
  const syncedPageIds = new Set(normalizeAnalyticsPageIds([...pageIdsNorm]));

  queryClient.invalidateQueries({
    predicate: (query) =>
      matchesSyncedAnalyticsQuery(query.queryKey, syncedPageIds, since, until),
  });

  store.dispatch<Dispatch>(PageThunk.requestPages());
  queryClient.invalidateQueries(queryKey.analyticsHealth());
}
